import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import Image from 'components/Common/Image'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import Group from 'components/utils/Group'
import type { BasicTrip } from 'tripPlanner/types/common'
import { formatCasualDateRange, getTripURL } from 'tripPlanner/utils'
import { getDestinationsLabel } from 'tripPlanner/utils/places'
import Sheet from 'components/utils/Sheet'
import NestableClickable from 'components/Common/NestableClickable'
import OptionsButton from 'tripPlanner/components/Documents/OptionsButton'

const StyledClickable = styled(NestableClickable)`
  display: block;
  border-bottom: 1px solid ${props => props.theme.palette.neutral.default.six};

  &:last-child {
    border-bottom: none;
  }
`

const MenuActions = styled.div`
  display: flex;
  flex-direction: column;
`

const MenuButton = styled.button`
  background: none;
  border: none;
  padding: ${rem(8)} ${rem(16)};
  text-align: left;
  cursor: pointer;
  font-size: ${rem(14)};
  color: ${props => props.theme.palette.neutral.default.one};

  &:hover {
    background-color: ${props => props.theme.palette.neutral.default.seven};
  }
`

interface Props {
  trip: BasicTrip
  onClick?: () => void
  onDeleteClick: (tripId: string, closeMenu: () => void) => (event: React.MouseEvent) => void
}

function DrawerTripListItem({ trip, onClick, onDeleteClick }: Props) {
  const intl = useIntl()

  const destinationsGeo = trip.destinationsGeo
  const hasDestination = destinationsGeo && destinationsGeo.length > 0
  const noDestinationLabel = intl.formatMessage({
    id: 'X2Uv6L7w2cUnfUtlrbSA',
    defaultMessage: 'No destination set',
    description: 'Fallback text when a trip has no destination in the drawer trip list',
  })
  const destinationText = hasDestination
    ? getDestinationsLabel(destinationsGeo, 1)
    : noDestinationLabel

  const hasDates = trip.startDate && trip.endDate
  const noDatesLabel = intl.formatMessage({
    id: 'hPi5q5dRmx6uUAY11gNu',
    defaultMessage: 'No dates added',
    description: 'Fallback text when a trip has no dates in the drawer trip list',
  })
  const dateText = hasDates
    ? formatCasualDateRange(trip.startDate!, trip.endDate)
    : noDatesLabel

  const tripActionsLabel = intl.formatMessage({
    id: '3VJ6QlvfdQdt7iHnZ58y',
    defaultMessage: 'Trip actions',
    description: 'Accessible title for the trip options menu button in the drawer',
  })
  const deleteLabel = intl.formatMessage({
    id: 'Jp_lIRegyPJ1-xyn0DHR',
    defaultMessage: 'Delete',
    description: 'Delete button label in the trip actions menu in the drawer',
  })

  return (
    <StyledClickable to={getTripURL(trip.id)} onClick={onClick}>
      <Sheet padding="12 0">
        <Group direction="horizontal" gap={16}>
          <Image
            id={trip.imageId}
            width={56}
            height={56}
          />
          <Group direction="horizontal" horizontalAlign="space-between" fullWidth verticalAlign="center">
            <Group direction="vertical" gap={2}>
              <BodyText variant="large">
                {trip.name}
              </BodyText>
              <BodyTextBlock
                variant="small"
                colour={hasDestination ? 'neutral-one' : 'neutral-three'}
                startIcon={<LineMapMarkerIcon size="XS" />}
              >
                {destinationText}
              </BodyTextBlock>
              <BodyTextBlock
                variant="small"
                colour={hasDates ? 'neutral-one' : 'neutral-three'}
                startIcon={<LineCalendarIcon size="XS" />}
              >
                {dateText}
              </BodyTextBlock>
            </Group>
            <OptionsButton
              title={tripActionsLabel}
              bodyContent={(closeMenu) => (
                <MenuActions>
                  <MenuButton
                    type="button"
                    onClick={onDeleteClick(trip.id, closeMenu)}
                  >
                    {deleteLabel}
                  </MenuButton>
                </MenuActions>
              )}
            />
          </Group>
        </Group>
      </Sheet>
    </StyledClickable>
  )
}

export default DrawerTripListItem
